import type { PlatformSyncResult } from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { usePublicSession } from "@/lib/context/public-session-context";
import { useInvalidateFlowCompletion } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import { goalSnapshotQueryKeys } from "@/lib/hooks/use-flow-instances/use-goal-snapshots";
import { goalQueryKeys } from "@/lib/hooks/use-flow-instances/use-goals";
import { usePlatformSyncWarnings } from "@/lib/hooks/use-flow-instances/use-platform-sync-warnings";
import { useInvalidatePortfolioSnapshots } from "@/lib/hooks/use-flow-instances/use-portfolio-snapshots";
import {
  invalidateGoalPortfolios,
  invalidatePortfolioFamily,
  useInvalidateDynamicSync,
} from "@/lib/hooks/use-flow-instances/use-portfolios";
import { goalPlatformQueryKeys } from "@/lib/hooks/use-goal-platforms";
import { useInvalidatePublicPortfolioSnapshots } from "@/lib/hooks/use-public-flow/use-goal-snapshots";
import { publicGoalQueryKeys } from "@/lib/hooks/use-public-flow/use-goals";
import {
  invalidatePublicGoalPortfolios,
  invalidatePublicPortfolioFamily,
  useInvalidatePublicDynamicSync,
} from "@/lib/hooks/use-public-flow/use-portfolios";
import { publicFlowCompletionQueryKey } from "@/lib/hooks/use-public-flow/use-public-flow-completion";

type CascadeArgs = {
  goalId: string;
  flowInstanceId: string;
};

/**
 * QP-195 — post-platform-change bookkeeping shared by every place a goal's
 * platform can change (the goal details widget, the goal list's edit modal):
 * warn about removed products, refresh the affected portfolio snapshots and
 * dynamic portfolios, and refetch the goal's platform state, the goals list
 * and the completion preflight on the right surface.
 */
export function useGoalPlatformCascade({ goalId, flowInstanceId }: CascadeArgs) {
  const { isPublic } = usePublicSession();
  const showPlatformSyncWarnings = usePlatformSyncWarnings();
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  const invalidatePortfolioSnapshots = useInvalidatePortfolioSnapshots();
  const invalidatePublicPortfolioSnapshots = useInvalidatePublicPortfolioSnapshots();
  const invalidateDynamicSync = useInvalidateDynamicSync();
  const invalidatePublicDynamicSync = useInvalidatePublicDynamicSync();

  return (sync: Partial<PlatformSyncResult> | undefined) => {
    showPlatformSyncWarnings(sync);
    for (const hint of sync?.refreshHints ?? []) {
      if (isPublic) invalidatePublicPortfolioSnapshots(hint.portfolioId, hint);
      else invalidatePortfolioSnapshots(hint.portfolioId, hint);
    }
    // The platform change may have REMOVED custom products the new platform
    // excludes: the goal's portfolio list and every portfolio's product rows
    // re-read, or the builder keeps showing rows that are gone and their
    // delete answers 404 (found testing QL-375).
    if (isPublic) {
      invalidatePublicGoalPortfolios(queryClient, goalId);
      invalidatePublicPortfolioFamily(queryClient);
    } else {
      invalidateGoalPortfolios(queryClient, goalId);
      invalidatePortfolioFamily(queryClient);
    }
    // QP-185 — the platform change regenerated dynamic portfolios.
    if (isPublic) invalidatePublicDynamicSync(sync?.dynamicSync);
    else invalidateDynamicSync(sync?.dynamicSync);
    queryClient.invalidateQueries({ queryKey: goalPlatformQueryKeys.state(goalId) });
    if (isPublic) {
      queryClient.invalidateQueries({ queryKey: publicGoalQueryKeys.goals() });
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
    } else {
      queryClient.invalidateQueries({ queryKey: goalQueryKeys.goals(flowInstanceId) });
      invalidateCompletion(flowInstanceId);
      // QL-375 — a platform change re-resolves the goal's mandate (template
      // tags follow the platform; a template-derived proposal is cleared).
      queryClient.invalidateQueries({ queryKey: goalSnapshotQueryKeys.mandate(goalId) });
    }
  };
}
